/**
 * Smart tooltips with acknowledgement timeout and forced view when pressing modifier key (Ctrl)
 *
 * shared by both hosts:
 *
 *   - the dashboard SPA (src/main.tsx), bundled by Vite, and
 *   - the /view banner client (src/banner/main.ts), bundled into the static /banner.js asset by the same Vite build.
 *
 * (Historically the dashboard implemented this in src/tips.ts and the banner re-implemented it in banner.js, but the two implementations had drifted apart. It is better to have a single authoritative implementation.)
 *
 * Contract — any element with:
 *   - [data-tip]      plain-text tip (safe: textContent)
 *   - [data-tip-html] rich HTML tip (server-escaped; supports .tip-title, .tip-list, code, links)
 *   - [data-tipkey]   stable tooltip identifier. Once acknowledged, the tip do not show again on pure hover without pressing modifier key (Ctrl). (acknowledgements live in localStorage under `nitter:tips-acked`, owned by src/settings.ts)
 *
 * Behaviour
 *   - A tip shows on hover; the progress bar fills over some seconds (or the user clicks the element) and it is then acknowledged and stays hidden.
 *   - The hint line under the text is ALWAYS visible: it explains that holding Ctrl or Shift pauses the countdown.
 *   - Holding Ctrl or Shift pauses the countdown: the progress bar freezes at its current width and resumes with the exact remaining time on key-up.
 *   - When keyboard focus lives inside a cross-origin iframe (the banner's embedded Nitter page), key events never reach this document, so a held modifier key (Ctrl/Shift) is undetectable. While that is the case, the countdown freezes on its own and resumes when focus returns to the host page. A visible tip is never acked while its reader's keyboard is parked in embedded content.
 *   - Acknowledged tips come back in a reference mode by holding a modifier key (Ctrl / Shift) while hovering. No progress bar, nothing is re-acknowledged. Pressing the modifier while already hovering the element resurfaces the tip without moving the mouse; releasing it hides the reference tip again.
 *   - Clicking a tipped element acknowledges its tip immediately.
 *   - Window blur hides the tip and forgets any held keys.
 *
 * The tooltip element and its stylesheet are created and injected here, so hosts don't render diverging tooltip markup of their own.
 */
import cssText from "./tip-engine.css?raw";
// noinspection ES6PreferShortImport // Need relative imports
import { ackTip, getTipsAcked } from "../settings";

const ACK_MS = 6000; // dwell time before a tip acknowledges itself
/** Resurface acknowledged tips. */
const MOD_KEYS = new Set(["Alt", "Control", "Shift", "Meta"]);
/** Pause the acknowledgement countdown while held. */
const PAUSE_KEYS = new Set(["Alt", "Control", "Shift", "Meta"]);

let tooltipEl: HTMLDivElement | null = null;
let tipTextEl: HTMLElement | null = null;
let tipHintEl: HTMLElement | null = null;
let tipFillEl: HTMLElement | null = null;

let currentTarget: Element | null = null;
let currentKey: string | null = null;
let ackTimer: number | null = null;
let hideTimer: number | null = null;
/** While a tip is visible, watch for keyboard focus silently moving into (or out of) a cross-origin iframe. Moving focus INTO such an iframe fires no event in this document at all, so a poll is the only way. It is cheap enough, and it only runs while a tooltip is on screen. */
let pollForKeyboardFocus: number | null = null;
let shownAt = 0; // when the current countdown started
let pausedRemaining: number | null = null; // ms left while paused (null = running)
// Last tipped element under the pointer + last cursor position, so a keydown can resurface the tip without a fresh mouseover event.
let hoverEl: Element | null = null;
type Coordinates = {x: number; y: number};
type TipPos = { clientX: number; clientY: number };
let lastMouse: Coordinates = { x: 0, y: 0 };
const heldPauseKeys = new Set<string>();
// True while the countdown is frozen because the keyboard focus sits in a cross-origin iframe (whose key events we cannot see).
let iframeFocused: boolean = false;
function keyboardInIframe(): boolean { return document.activeElement instanceof HTMLIFrameElement; }

const holdCtrlOrShiftToShowTooltip: string = "Hold Ctrl or Shift to show tooltips";
const HINT_NORMAL: string = holdCtrlOrShiftToShowTooltip + " after they have been dismissed.";
const HINT_FORCED: string = holdCtrlOrShiftToShowTooltip + "."; // "Reference mode — release key to hide."
/** Any modifier held, according to a mouse event (works even when keyboard focus is inside an iframe). */
function modHeld(e: MouseEvent): boolean {
    return e.ctrlKey || e.shiftKey || e.altKey || e.metaKey;
}
let parkedIframe: HTMLIFrameElement | null = null;

/** Pull keyboard focus out of an embedded iframe so Ctrl/Shift keydowns reach this document. */
function reclaimKeyboard(): void {
    const a = document.activeElement;
    if (a instanceof HTMLIFrameElement) {
        parkedIframe = a;
        a.blur();
        window.focus();
    }
}
/** Give the iframe its keyboard focus back once the pointer leaves the tipped element. */
function returnKeyboard(): void {
    const f = parkedIframe;
    parkedIframe = null;
    if (f && document.activeElement === document.body) f.focus();
}
/** inject tip-engine.css if it was not added yet to the document */
function ensureTooltipCssWasInjected() {
    if (!document.getElementById("tip-engine-css")) {
        const style = document.createElement("style");
        style.id = "tip-engine-css";
        style.textContent = cssText;
        document.head.appendChild(style);
    }
}

function createTooltipMarkup(): HTMLDivElement {
    tooltipEl = document.createElement("div");
    // noinspection SpellCheckingInspection
    tooltipEl.id = "smarttip";
    tooltipEl.setAttribute("role", "tooltip");
    tooltipEl.innerHTML = '<div class="tip-text"></div>' +
        '<span class="tip-hint"></span>' +
        '<div class="tip-progress"><div class="tip-progress-fill"></div></div>';
    tipTextEl = tooltipEl.querySelector<HTMLElement>(".tip-text");
    tipHintEl = tooltipEl.querySelector<HTMLElement>(".tip-hint");
    tipFillEl = tooltipEl.querySelector<HTMLElement>(".tip-progress-fill");
    document.body.appendChild(tooltipEl);
    return tooltipEl;
}

/** @return memoized or newly created tooltip Element (<div/>) */
function ensureTooltip(): HTMLDivElement {
if (tooltipEl) return tooltipEl;
ensureTooltipCssWasInjected();
return createTooltipMarkup();
}
/** hide tooltip and reset callbacks */
function hideNow(): void {
if (ackTimer != null) {clearTimeout(ackTimer);ackTimer = null;}
if (hideTimer != null) {clearTimeout(hideTimer);hideTimer = null;}
if (pollForKeyboardFocus != null) {clearInterval(pollForKeyboardFocus);pollForKeyboardFocus = null;}
pausedRemaining = null;
iframeFocused = false;
currentTarget = null;
currentKey = null;
tooltipEl?.classList.remove("show", "forced", "paused");
}
/** While a tip is visible, watch for keyboard focus silently moving into (or out of) a cross-origin iframe. Moving focus INTO such an iframe fires no event in this document at all, so a poll is the only way. It is cheap enough, and it only runs while a tooltip is on screen. */
function startFocusPoll(): void {
  if (pollForKeyboardFocus != null) return;
  pollForKeyboardFocus = window.setInterval(() => {
    if (keyboardInIframe()) {
      if (!iframeFocused && ackTimer != null) {
        iframeFocused = true;
        pauseAck();
      }
    } else if (iframeFocused) {
      iframeFocused = false;
      resumeAck();
    }
  }, 250);
}

/** Hide whatever tip is currently visible, if any. Exported for hosts that move things under the cursor (e.g. the banner's draggable pill) and want the tooltip gone instead of trailing the pointer. */
export function hideActiveTip(): void { hideNow(); }
/** Small delay so moving between adjacent tipped elements doesn't flicker. */
function hideSoon(): void {
  if (hideTimer != null) clearTimeout(hideTimer);
  hideTimer = window.setTimeout(hideNow, 80);
}
function isTipAckedKey(key: string | null): boolean { return !!key && getTipsAcked()[key] != null; }

/**restart the fill animation from its current width*/
function restartFillAnimationCss(fill: HTMLElement, animationDurationMs: number) {
    void fill.offsetWidth;
    fill.style.transition = `width ${animationDurationMs}ms linear`;
    fill.style.width = "100%";
}

/** (Re)start the acknowledgement countdown for `ms` from now. */
function runAck(durationMilliseconds: number): void {
  pausedRemaining = null;
  tooltipEl?.classList.remove("paused");
  shownAt = Date.now();
  const fill = tipFillEl;
  if (fill) restartFillAnimationCss(fill, durationMilliseconds);
  if (ackTimer != null) clearTimeout(ackTimer);
  ackTimer = window.setTimeout(() => {
    if (currentKey) ackTip(currentKey);
    hideNow();
  }, durationMilliseconds);
}
/** Modifier key pressed: freeze the countdown + progress bar. */
function pauseAck(): void {
  if (ackTimer == null || pausedRemaining != null || !currentTarget) return;
  const remaining = ACK_MS - (Date.now() - shownAt);
  if (remaining <= 0) return; // acking anyway
  clearTimeout(ackTimer);
  ackTimer = null;
  pausedRemaining = remaining;
  tooltipEl?.classList.add("paused");
  const fill: HTMLElement | null = tipFillEl;
  if (fill) {
    const w: string = getComputedStyle(fill).width; // freeze at current width
    fill.style.transition = "none";
    fill.style.width = w;
  }
}
/** Modifier key released: resume the countdown where it paused. */
function resumeAck(): void {
  if (heldPauseKeys.size > 0 || pausedRemaining == null) return;
  if (keyboardInIframe()) {
    // Keyboard still parked in the iframe (its keys are invisible to us): stay frozen. Focus poll will resume when it returns.
    iframeFocused = true;
    return;
  }
  const remaining = pausedRemaining;
  pausedRemaining = null;
  if (!currentTarget) return; // tip went away while paused
  runAck(remaining);
}

export function moveTooltip(tip: HTMLDivElement, e: TipPos, padLeft: number = 14, padTop: number = 18, padRight: number = 12, padBottom: number = 12) {
    const w: number = tip.offsetWidth;
    const h: number = tip.offsetHeight;
    let x: number = e.clientX + padLeft;
    let y: number = e.clientY + padTop;
    if (x + w + padRight > window.innerWidth) x = e.clientX - w - padLeft;
    if (y + h + padBottom > window.innerHeight) y = e.clientY - h - padTop;
    tip.style.left = Math.max(padRight, x) + "px";
    tip.style.top = Math.max(padBottom, y) + "px";
}
function moveTip(e: TipPos): void {
  const tip = tooltipEl;
    if (tip && tip.classList.contains("show")) { moveTooltip(tip, e); }
}
function show(el: Element, e: TipPos, forced: boolean): void {
  const html: string | null = el.getAttribute("data-tip-html");
  const text: string | null = el.getAttribute("data-tip");
  if (!html && !text) return;
  const tip = ensureTooltip();
  currentTarget = el;
// noinspection SpellCheckingInspection
  currentKey = el.getAttribute("data-tipkey") || null;
  if (hideTimer != null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  // The tooltip keeps persistent nodes; only the payload changes.
  if (tipTextEl) {
    if (html) tipTextEl.innerHTML = html; else tipTextEl.textContent = text;
  }
  if (tipHintEl) tipHintEl.textContent = forced ? HINT_FORCED : HINT_NORMAL;
  tip.classList.toggle("forced", forced);
  tip.classList.remove("paused");
  if (tipFillEl) {
    tipFillEl.style.transition = "none";
    tipFillEl.style.width = "0%";
  }
  if (!forced) {
    if (heldPauseKeys.size > 0) {
      // A pause key is already held: start the countdown frozen.
      pausedRemaining = ACK_MS;
      tip.classList.add("paused");
    } else if (keyboardInIframe()) {
      // Focus lives in the embedded (cross-origin) page, so a held modifier key would be invisible: start frozen and wait for the user to come back to the host page.
      pausedRemaining = ACK_MS;
      iframeFocused = true;
      tip.classList.add("paused");
    } else {
      runAck(ACK_MS);
    }
  }
  tip.classList.add("show");
  moveTip(e);
  startFocusPoll();
}
/** Wire up the document-level listeners.
 * @return a cleanup function. */
export function setupTipEngine(): () => void {
const onOver = (e: MouseEvent) => {
const el = (e.target as Element | null)?.closest?.("[data-tip], [data-tip-html]") as Element | null;
hoverEl = el;
if (!el) {
  if (currentTarget && !(e.target as Element | null)?.closest?.("#smarttip")) {hideSoon();}
  return;
}
reclaimKeyboard();
    if (el === currentTarget) return; // already showing for this element
    if (hideTimer != null) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
    const key = el.getAttribute("data-tipkey");
    const acked: boolean = isTipAckedKey(key);
    const forced: boolean = acked && modHeld(e);
    if (acked && !forced) {
        // B is acknowledged and no modifier is held: stay quiet, but don't leave A's tip behind.
        if (currentTarget) hideNow();
        return;
    }
    show(el, e, forced);
  };
    const onMove = (e: MouseEvent) => {
        lastMouse.x = e.clientX; lastMouse.y = e.clientY;
        const visible = !!tooltipEl?.classList.contains("show");
        const mod = modHeld(e);

        if (visible) {
            moveTip(e);
            // Reference tip is up but the modifier was released while keyup was invisible to us (iframe focused).
            if (!mod && tooltipEl!.classList.contains("forced")) hideNow();
            return;
        }
        // Modifier pressed while hovering an acked tip; keydown never reached us, but the mouse event tells us.
        if (mod && hoverEl && isTipAckedKey(hoverEl.getAttribute("data-tipkey"))) {
            show(hoverEl, e, true);
        }
    };
const onOut = (e: MouseEvent) => {
    const from = e.target as Element | null;
    const to = e.relatedTarget as Element | null;
    if (from?.closest?.("[data-tip], [data-tip-html]") && !to?.closest?.("[data-tip], [data-tip-html]")) {
      hoverEl = null;
    returnKeyboard();
      hideSoon();
    }
};
  const onKeydown = (e: KeyboardEvent) => {
    if (PAUSE_KEYS.has(e.key)) {
      heldPauseKeys.add(e.key);
      pauseAck();
    }
    if (!MOD_KEYS.has(e.key) || !hoverEl) return;
    if (tooltipEl?.classList.contains("show")) return; // already visible
    const key = hoverEl.getAttribute("data-tipkey");
    if (!isTipAckedKey(key)) return; // not acked → would already show
    show(hoverEl, { clientX: lastMouse.x, clientY: lastMouse.y }, true);
  };
  const onKeyup = (e: KeyboardEvent) => {
    if (PAUSE_KEYS.has(e.key)) {
      heldPauseKeys.delete(e.key);
      resumeAck();
    }
    if (MOD_KEYS.has(e.key)) {
      // Releasing the modifier hides a forced (reference) tip again.
      if (tooltipEl?.classList.contains("show") && tooltipEl.classList.contains("forced")) {
        hideNow();
      }
    }
  };
const onBlur = () => {
    // Keys held will have their keyup delivered elsewhere, so never trust them.
    heldPauseKeys.clear();
    // Focus moving INTO a cross-origin iframe fires window blur, but the user is still on this page: keep the tip.
    setTimeout(() => { if (!keyboardInIframe()) hideNow(); }, 0);
};
    const onFocus = () => { heldPauseKeys.clear(); };
    const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest?.("[data-tip], [data-tip-html]") as Element | null;
    if (!el) return;
    const key = el.getAttribute("data-tipkey");
    if (key) ackTip(key);
    if (el === currentTarget) hideNow();
  };
  document.addEventListener("mouseover", onOver);
  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseout", onOut);
  document.addEventListener("click", onClick, true);
  document.addEventListener("keydown", onKeydown);
  document.addEventListener("keyup", onKeyup);
  window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
  return () => {
    document.removeEventListener("mouseover", onOver);
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseout", onOut);
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("keydown", onKeydown);
    document.removeEventListener("keyup", onKeyup);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("focus", onFocus);
    hideNow();
  };
}